<template>
  <HrPageLayout
    :title="pageTitle"
    :subtitle="pageSubtitle"
    :breadcrumb-items="breadcrumbItems"
  >
    <div class="single-report-content">
      <div class="filters-card">
        <div class="filters-grid">
          <div class="filter-group">
            <label class="filter-label">From Date</label>
            <input v-model="filters.fromDate" type="date" class="filter-input">
          </div>
          <div class="filter-group">
            <label class="filter-label">To Date</label>
            <input v-model="filters.toDate" type="date" class="filter-input">
          </div>
          <div v-if="showDepartmentFilter" class="filter-group">
            <label class="filter-label">Department</label>
            <select v-model="filters.departmentId" class="filter-select">
              <option value="">All</option>
              <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.departmentName }}</option>
            </select>
          </div>
          <div v-if="reportType === 'attrition'" class="filter-group">
            <label class="filter-label">Attrition type</label>
            <select v-model="filters.separationType" class="filter-select">
              <option value="">All</option>
              <option v-for="t in attritionTypes" :key="t" :value="t">{{ t }}</option>
            </select>
          </div>
          <div class="filter-group align-end">
            <button type="button" class="hr-btn hr-btn-primary" @click="loadReport" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
              Apply
            </button>
            <button type="button" class="hr-btn hr-btn-success" @click="downloadReport" :disabled="loading || !reportData.length">
              Download CSV
            </button>
          </div>
        </div>
      </div>

      <div class="table-card">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary"></div>
          <p class="mt-2">Loading report...</p>
        </div>
        <div v-else-if="!reportData.length" class="text-center py-5 text-muted">
          No data for the selected filters. Adjust dates and click Apply.
        </div>
        <div v-else class="table-responsive">
          <table class="table table-bordered table-sm">
            <thead>
              <tr>
                <th v-for="col in reportColumns" :key="col">{{ formatHeader(col) }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in reportData" :key="idx">
                <td v-for="col in reportColumns" :key="col">{{ row[col] }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl } from "@/api";

const REPORT_CONFIG = {
  department_staff_establishment: { title: 'Staff Establishment', subtitle: 'Active staff by department', section: 'Reports & Analytics', showDept: true },
  attrition: { title: 'Attrition Report', subtitle: 'Separations (death, resignation, dismissal)', section: 'Reports & Analytics', showDept: true },
  wealth_declaration_compliance: { title: 'Wealth Declaration Compliance', subtitle: 'Officers who submitted declarations', section: 'Reports & Analytics', showDept: true },
  wealth_declaration_non_compliant: { title: 'Wealth Non-Compliant Officers', subtitle: 'Active officers without submitted declaration', section: 'Reports & Analytics', showDept: true },
  department_wage_bill: { title: 'Department Wage Bill', subtitle: 'Wage bill by department', section: 'Reports & Analytics', showDept: true },
  county_wage_bill: { title: 'County Wage Bill', subtitle: 'County-wide wage bill (all departments)', section: 'Reports & Analytics', showDept: false }
};

export default {
  name: "SingleReport",
  components: { HrPageLayout },
  data() {
    return {
      filters: {
        fromDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        toDate: new Date().toISOString().slice(0, 10),
        departmentId: "",
        separationType: ""
      },
      attritionTypes: ["Resignation", "Dismissal", "Termination", "Retirement", "Death", "Transfers", "Other"],
      departments: [],
      reportData: [],
      reportColumns: [],
      loading: false
    };
  },
  computed: {
    reportType() {
      return this.$route.meta.reportType || 'department_staff_establishment';
    },
    config() {
      return REPORT_CONFIG[this.reportType] || REPORT_CONFIG.department_staff_establishment;
    },
    pageTitle() {
      return this.config.title;
    },
    pageSubtitle() {
      return this.config.subtitle;
    },
    showDepartmentFilter() {
      return this.config.showDept;
    },
    breadcrumbItems() {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: this.config.section },
        { label: this.config.title }
      ];
    }
  },
  watch: {
    '$route.name'() {
      this.loadReport();
    }
  },
  created() {
    this.getDepartments();
    this.loadReport();
  },
  methods: {
    getDepartments() {
      const fd = new FormData();
      fd.append("function", "getDepartments");
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data && res.data.data.departments) {
          this.departments = res.data.data.departments;
        }
      }).catch(() => {});
    },
    loadReport() {
      this.loading = true;
      const fd = new FormData();
      fd.append("function", "getReportsHubData");
      fd.append("reportType", this.reportType);
      if (this.filters.fromDate) fd.append("fromDate", this.filters.fromDate);
      if (this.filters.toDate) fd.append("toDate", this.filters.toDate);
      if (this.filters.departmentId) fd.append("departmentId", this.filters.departmentId);
      if (this.reportType === 'attrition' && this.filters.separationType) fd.append("separationType", this.filters.separationType);
      execute(fd, authUrl)
        .then((res) => {
          this.loading = false;
          if (res.data && res.data.success && res.data.data) {
            this.reportData = res.data.data.data || [];
            this.reportColumns = (res.data.data.columns && res.data.data.columns.length)
              ? res.data.data.columns
              : (this.reportData[0] ? Object.keys(this.reportData[0]) : []);
          } else {
            this.reportData = [];
            this.reportColumns = [];
          }
        })
        .catch(() => {
          this.loading = false;
          this.reportData = [];
          this.reportColumns = [];
        });
    },
    downloadReport() {
      const params = new URLSearchParams({
        function: "downloadReportsHubReport",
        reportType: this.reportType,
        fromDate: this.filters.fromDate || "",
        toDate: this.filters.toDate || "",
        departmentId: this.filters.departmentId || "",
        separationType: this.reportType === 'attrition' ? (this.filters.separationType || "") : ""
      });
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const url = `${authUrl}?${params.toString()}`;
      const xhr = new XMLHttpRequest();
      xhr.open("GET", url);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.responseType = "blob";
      xhr.onload = () => {
        if (xhr.status !== 200) return;
        const a = document.createElement("a");
        a.href = window.URL.createObjectURL(xhr.response);
        a.download = `report_${this.reportType}_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        window.URL.revokeObjectURL(a.href);
      };
      xhr.send();
    },
    formatHeader(col) {
      return String(col).replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();
    }
  }
};
</script>

<style scoped>
.single-report-content { padding: 0 0 24px; }
.filters-card { background: #fff; border: 1px solid #dee2e6; border-radius: 8px; padding: 16px; margin-bottom: 16px; }
.filters-grid { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.filter-group { display: flex; flex-direction: column; gap: 4px; }
.filter-group.align-end { margin-left: auto; }
.filter-label { font-size: 0.875rem; font-weight: 500; color: #495057; }
.filter-input, .filter-select { padding: 6px 10px; border: 1px solid #ced4da; border-radius: 6px; min-width: 140px; }
.table-card { background: #fff; border: 1px solid #dee2e6; border-radius: 8px; overflow: hidden; }
.table-responsive { overflow-x: auto; }
.hr-btn { padding: 8px 16px; border-radius: 6px; border: none; cursor: pointer; font-weight: 500; }
.hr-btn-primary { background: #0d6efd; color: #fff; }
.hr-btn-success { background: #198754; color: #fff; }
.hr-btn:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
